// TVEC templates shown on /tvec/template.
//
// RULE: the files under public/templates/tvec/originals are the official originals,
// copied byte-for-byte from the TEVC folder. Never regenerate, restyle, rename or
// "improve" them; to change a format, replace the file with a new original
// (see CLAUDE.md, "TVEC / ESOFT template formats must stay exactly as supplied").
//
// tvecTemplates.manifest.json is generated from that folder (programme > process stage >
// file). Only the on-screen labels below are tidied; file names and contents are untouched.
import manifest from './tvecTemplates.manifest.json';

// Document codes come from the Documentation Standards:
//   F = Form, PL = Plan, R = Register, TL = Table, FL = File, PS = Practical Sheet,
//   H = Handout, WS = Work Sheet, B = Book.

// Process stages in the order the documents are used, with a one-line hint.
export const PROCESSES = [
  { id: 'curriculum', label: 'Curriculum', hint: 'Programme curriculum / specification' },
  { id: 'course-plan', label: 'Course plan', hint: 'Hours, weeks and delivery plan for the programme' },
  { id: 'timetable', label: 'Weekly plan & timetable', hint: 'Weekly plan and lecture timetable' },
  { id: 'lesson-plan', label: 'Lesson / session plans', hint: 'Session-by-session plans' },
  { id: 'registry', label: 'Student registry', hint: 'Registry of enrolled students' },
  { id: 'staff', label: 'Staff details', hint: 'Academic, coordinator and support staff' },
  { id: 'attendance', label: 'Student attendance', hint: 'Theory, practical and tutorial attendance sheets' },
  { id: 'practicals', label: 'Practicals', hint: 'Practical / lab sheets' },
  { id: 'tutorials', label: 'Tutorials', hint: 'Tutorial sheets' },
  { id: 'learning-material', label: 'Practicals, tutorials & self studies', hint: 'Module learning material' },
  { id: 'assignment-brief', label: 'Assignment briefs', hint: 'Assignment briefs and samples' },
  { id: 'assessment-plan', label: 'Assessment plan', hint: 'Delivery tools and assessment plan' },
  { id: 'mark-sheet', label: 'Mark sheets & assessment records', hint: 'Mark sheets, verification and assessment records' },
  { id: 'common', label: 'Common material', hint: 'Shared by every programme' },
  { id: 'typed', label: 'Typed from pasted text', hint: 'No original file supplied yet' },
];

export const PROGRAMMES = [
  { id: 'hnd', label: 'HND Computing', short: 'HND Computing' },
  { id: 'diit', label: 'Diploma in Information Technology (DIIT)', short: 'DIIT' },
  { id: 'dim', label: 'Diploma in Management (DIM)', short: 'DIM' },
  { id: 'common', label: 'Common material', short: 'Common' },
  { id: 'typed', label: 'Other formats (typed from pasted text)', short: 'Other', note: 'No original file was supplied for these. Send the original and it will replace the version here.' },
];

// HND session plans: file abbreviation -> module name (from the HND course plan).
const HND_MODULES = {
  Programming: 'Programming',
  DDD: 'Database Design & Development',
  Networking: 'Networking',
  PP: 'Professional Practice',
  PACP: 'Planning a Computing Project',
  Security: 'Security',
  SDLC: 'Software Development Lifecycles',
  WDD: 'Website Design & Development',
  CRP: 'Computing Research Project',
  BPS: 'Business Process Support',
  IOT: 'Internet of Things',
  ET: 'Emerging Technologies',
  DM: 'Discrete Maths',
  DSA: 'Data Structure and Algorithms',
  APDP: 'Applied Programming and Design Principles',
};

// On-screen spelling fixes only (the real file name is always shown next to the label).
const LABEL_FIXES = [
  [/Dodumentation/g, 'Documentation'],
  [/Continouse Assesment/g, 'Continuous Assessment'],
  [/Attendence/g, 'Attendance'],
  [/Temparary/g, 'Temporary'],
  [/Descrete/g, 'Discrete'],
];
const tidy = (text) => LABEL_FIXES.reduce((s, [re, to]) => s.replace(re, to), text);

function displayName(item) {
  const hndPlan = /^Session Plan - (.+)$/.exec(item.name);
  if (item.programme === 'hnd' && item.process === 'lesson-plan' && hndPlan && HND_MODULES[hndPlan[1]]) {
    return `Session Plan – ${HND_MODULES[hndPlan[1]]}`;
  }
  if (item.name === 'Mark sheet (WS1)') return 'Mark Sheet';
  return tidy(item.name);
}

function displayCode(item) {
  // Only the HND "Mark sheet (WS1)" is a WS1 form; the verification / witness / observation
  // records and the Pearson criteria are separate Pearson documents without a TVEC code.
  if (item.programme === 'hnd' && item.process === 'mark-sheet') return item.name === 'Mark sheet (WS1)' ? 'WS1' : '';
  // Attendance sheets: Theory F1, Practical F2, Tutorial F3, whatever prefix the file carries.
  if (item.process === 'attendance') {
    const low = item.name.toLowerCase();
    if (low.includes('practical')) return 'F2';
    if (low.includes('tutorial')) return 'F3';
    return 'F1';
  }
  if (item.process === 'timetable' && !item.code && /time table/i.test(item.name)) return 'TL1';
  if (item.process === 'common' && /annual training plan/i.test(item.name)) return 'PL3';
  return item.code || '';
}

const subLabel = (sub) => tidy(sub.replace(/^H1 DIM Tutorial /, ''));

const fromManifest = manifest.items.map((item) => ({
  id: item.path,
  programme: item.programme,
  process: item.process,
  sub: subLabel(item.sub || ''),
  code: displayCode(item),
  name: displayName(item),
  file: item.file,
  kind: item.kind, // excel | word | pdf
  size: item.size,
  urlPath: `originals/${item.path}`,
}));

// Typed from text pasted into the chat, not from an original file. They carry styling added
// while building them. Replace each with its original when one is supplied.
const typed = [
  {
    id: 'typed-annual-training-plan-format-03',
    name: 'Annual Training Plan (Format 03)',
    code: 'PL3',
    file: 'Annual_Training_Plan_Format_03.xlsx',
    headerRows: 2,
  },
  {
    id: 'typed-assessment-results-format-04',
    name: 'Assignment / Practical Results Sheet (Format 04)',
    code: 'PL4',
    file: 'Assessment_Results_Sheet_Format_04.xlsx',
  },
  {
    id: 'typed-sample-timetable-format-06',
    name: 'Sample Timetable (Format 06)',
    code: 'TL1',
    file: 'Sample_Timetable_Format_06.xlsx',
  },
  {
    id: 'typed-documentation-checklist',
    name: 'TVEC Documentation Checklist',
    code: 'CL1',
    file: 'TVEC_Documentation_Checklist.xlsx',
  },
  {
    id: 'typed-batch-entry-upload-instructions',
    name: 'Batch Entry Upload Instructions',
    code: '',
    file: 'Batch_Entry_Upload_Instructions_Format_05.docx',
  },
].map((t) => ({
  ...t,
  programme: 'typed',
  process: 'typed',
  sub: '',
  kind: t.file.endsWith('.docx') ? 'word' : 'excel',
  size: 0,
  urlPath: t.file,
  legacy: true,
}));

export const TEMPLATE_ITEMS = [...fromManifest, ...typed];

export const templateUrl = (item) =>
  `${process.env.PUBLIC_URL || ''}/templates/tvec/${item.urlPath.split('/').map(encodeURIComponent).join('/')}`;

export const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const KIND_LABEL = { excel: 'Excel', word: 'Word', pdf: 'PDF' };
